/**
 * AdminImprevistosA5.jsx · Agente A5 — Imprevistos + alertas + reasignación.
 */
import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  HeartPulse, Plus, RefreshCw, CheckCircle, AlertTriangle, Users,
  Loader2, X, Save, Radio, Clock,
} from "lucide-react";

const CATEGORIES = [
  { key: "enfermedad", label: "Enfermedad" },
  { key: "falla_mecanica", label: "Falla mecánica" },
  { key: "ausencia", label: "Ausencia" },
  { key: "accidente", label: "Accidente" },
  { key: "otro", label: "Otro" },
];

export default function AdminImprevistosA5() {
  const [tab, setTab] = useState("calamities");
  const [calamities, setCalamities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, a, d, u] = await Promise.all([
        api.get("/a5/calamities?status=open"),
        api.get("/a5/alerts?acknowledged=false"),
        api.get("/a5/dashboard"),
        api.get("/users/assignments").catch(() => ({ data: { items: [] } })),
      ]);
      setCalamities(c.data.items || []);
      setAlerts(a.data.items || []);
      setDashboard(d.data);
      setUsers(u.data.items || []);
    } catch (e) { toast.error("Error cargando A5"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runScan = async () => {
    try { await api.post("/a5/run-scan"); toast.success("Scan ejecutado"); load(); }
    catch (e) { toast.error("Error"); }
  };
  const resolveCalamity = async (id) => {
    if (!window.confirm("¿Marcar como resuelta?")) return;
    try { await api.post(`/a5/calamity/${id}/resolve`); toast.success("Resuelta"); load(); }
    catch (e) { toast.error("Error"); }
  };
  const ackAlert = async (id) => {
    try { await api.post(`/a5/alerts/${id}/ack`); load(); }
    catch (e) { toast.error("Error"); }
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="a5-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
                 style={{ background: "#FCE7F3", border: "1px solid #F472B6" }}>
              <HeartPulse size={11} className="text-pink-700" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-pink-700">Agente A5 · Reasignación dinámica</span>
            </div>
            <h1 className="text-3xl font-bold" style={{ color: "#0A3747" }}>Imprevistos &amp; alertas</h1>
            <p className="mt-1 text-sm text-gray-500">Enfermedad · falla mecánica · ausencia · pérdida GPS · SLA breach. Todo dispara reasignación automática.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={runScan} className="neu-btn" data-testid="btn-run-scan"><Radio size={14}/> Escanear ahora</button>
            <button onClick={() => setShowNew(true)} className="neu-btn neu-btn-primary" data-testid="btn-new-calamity"><Plus size={14}/> Declarar imprevisto</button>
          </div>
        </div>

        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <KPI label="Imprevistos abiertas" value={dashboard.open_calamities} color="#DC2626" icon={HeartPulse}/>
            <KPI label="Alertas sin ack" value={Object.values(dashboard.alerts_by_type || {}).reduce((a,b) => a+b, 0)} color="#F59E0B" icon={AlertTriangle}/>
            <KPI label="OTs pending re-agenda" value={dashboard.pending_reschedule_ots} color="#7C3AED" icon={RefreshCw}/>
          </div>
        )}

        <div className="flex items-center gap-2 border-b">
          <TabBtn active={tab === "calamities"} onClick={() => setTab("calamities")} label={`Imprevistos (${calamities.length})`} testid="tab-calam"/>
          <TabBtn active={tab === "alerts"} onClick={() => setTab("alerts")} label={`Alertas (${alerts.length})`} testid="tab-alerts"/>
        </div>

        {loading ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-gray-400"/></div>
        : tab === "calamities" ? (
          <div className="space-y-2">
            {calamities.length === 0 ? <div className="neu-card p-10 text-center text-sm text-gray-500">Sin imprevistos abiertas.</div>
            : calamities.map(c => (
              <div key={c.id} className="rounded-xl border-2 p-4" style={{ background: "#FEF2F2", borderColor: "#F87171" }} data-testid={`calam-${c.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-base" style={{ color: "#0A3747" }}>{c.user_name}</h3>
                      <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-red-600 text-white">{c.category}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">{c.email}</div>
                    {c.description && <div className="text-xs italic text-gray-700 mt-1">{c.description}</div>}
                    <div className="text-[11px] text-gray-500 mt-1">
                      Desde: {new Date(c.starts_at).toLocaleString("es-EC")} · {c.affected_ot_count} OTs afectadas · por {c.created_by}
                    </div>
                  </div>
                  <button onClick={() => resolveCalamity(c.id)} className="neu-btn text-xs" data-testid={`resolve-${c.id}`}>
                    <CheckCircle size={12}/> Resolver
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {alerts.length === 0 ? <div className="neu-card p-10 text-center text-sm text-gray-500">Sin alertas pendientes.</div>
            : alerts.map(a => (
              <div key={a.id} className="rounded-lg border p-3 bg-white" data-testid={`alert-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${a.severity === "critical" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                    <AlertTriangle size={16}/>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold">
                      <span className={a.severity === "critical" ? "text-red-700" : "text-amber-700"}>{a.alert_type}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-500">{new Date(a.created_at).toLocaleString("es-EC")}</span>
                    </div>
                    <div className="text-sm mt-0.5" style={{ color: "#0A3747" }}>{a.message}</div>
                    {a.user_name && <div className="text-[11px] text-gray-500">Técnico: {a.user_name}</div>}
                  </div>
                  <button onClick={() => ackAlert(a.id)} className="text-xs px-2 py-1 rounded border" data-testid={`ack-${a.id}`}>
                    Ack
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showNew && <NewCalamityModal users={users} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }}/>}
    </Layout>
  );
}

function KPI({ label, value, color, icon: Icon }) {
  return (
    <div className="neu-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold" style={{ color }}>
        <Icon size={12}/> {label}
      </div>
      <div className="text-3xl font-bold mt-1 tabular-nums" style={{ color: "#0A3747" }}>{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-2 text-sm font-semibold border-b-2 ${active ? "border-[#0A3747] text-[#0A3747]" : "border-transparent text-gray-500"}`}>
      {label}
    </button>
  );
}

function NewCalamityModal({ users, onClose, onSaved }) {
  const [userId, setUserId] = useState("");
  const [category, setCategory] = useState("enfermedad");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!userId) { toast.error("Selecciona técnico"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/a5/calamity", { user_id: userId, category, description });
      toast.success(`Imprevisto declarada · ${data.affected_ot_count} OTs para reasignar`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="new-calam-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full flex flex-col shadow-2xl">
        <div className="flex items-start justify-between p-5 border-b" style={{ background: "#FEE2E2" }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-red-700">Nuevo imprevisto</div>
            <h2 className="text-xl font-bold mt-1" style={{ color: "#0A3747" }}>Declarar</h2>
          </div>
          <button onClick={onClose} data-testid="calam-close"><X size={22}/></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Técnico *</label>
            <select value={userId} onChange={(e) => setUserId(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" data-testid="calam-user">
              <option value="">— Selecciona —</option>
              {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} · {u.email}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Categoría *</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" data-testid="calam-cat">
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Descripción</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows="3" className="w-full px-3 py-2 border rounded text-sm" data-testid="calam-desc"/>
          </div>
          <div className="text-[10px] text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">
            Todas las OTs pendientes del técnico se marcarán para reasignar automáticamente.
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 bg-gray-50">
          <button onClick={onClose} className="neu-btn" data-testid="calam-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="neu-btn neu-btn-primary" data-testid="calam-save">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Declarar
          </button>
        </div>
      </div>
    </div>
  );
}
